/**
 * 应用升级检测服务
 *
 * 升级策略（安卓）：
 *  1. 请求版本管理后台（静态资源管理系统）检查更新接口
 *  2. 接口 versionCode 高于本机原生 versionCode → 跳转升级页，下载安装包 (apk) 或跳转应用市场
 *  3. 无需更新：静默跳过；主动检测时提示"已是最新"
 * 说明：新数据源无强制更新与热更语义（force 恒 false，wgt 热更分支已随旧协议废弃移除）
 *
 * 升级策略（iOS）：
 *  1. 通过 iTunes Search API 查询 App Store 最新版本
 *  2. 有新版本时跳转升级页，跳转 App Store 链接
 *  3. appleId 为空时跳过检测
 *
 * Storage Key：
 *  - upgrade_data：升级接口返回的完整数据，升级页读取使用
 */

import { getUpgradeConfig } from '@/project';
import { systemInfo } from '@/utils/systemInfo';

const UPGRADE_STORAGE_KEY = 'upgrade_data';

/** 升级页消费的数据结构（接口 data 归一化产物，保持旧字段名以兼容升级页） */
export interface UpgradeData {
  /** 远程最新版本号 (semver) */
  version: string
  /** 远程最新构建版本号 (整数) */
  version_code: string
  /** 安装包下载地址 */
  download_url: string
  /** 版本更新说明 */
  desc: string
  /** 是否强制更新（新数据源无强制语义，恒 false） */
  force: boolean
  /** 是否需要热更新（新数据源无热更语义，恒 false） */
  hot: boolean
  /** 热更新包地址 */
  hot_url?: string
  /** 热更新说明 */
  hot_desc?: string
}

/** 版本管理后台检查更新接口 data（新协议，与 seller-store-h5 同源） */
interface LatestVersionData {
  versionNo: string
  versionCode: number
  note: string | null
  apkUrl: string
  fileUrl?: string
  fileSize?: string
  fileType?: string
  publishTime?: number
}

/** 版本管理后台接口统一响应包裹 */
interface ApiResponse {
  status: number
  code: number
  msg?: string
  data: LatestVersionData | null
}

/** iTunes API 返回数据（iOS） */
interface ITunesLookupResult {
  resultCount: number
  results: Array<{
    version: string
    trackViewUrl: string
    trackName: string
    releaseNotes?: string
  }>
}

/**
 * 对比新旧版本号，判断是否需要更新
 * @param oldVersion 当前版本
 * @param newVersion 远程版本
 * @returns true = 需要更新
 */
export function compareVersion(oldVersion: string, newVersion: string): boolean {
  if (!oldVersion || !newVersion) return false;

  const oldParts = String(oldVersion).split('.').map(Number);
  const newParts = String(newVersion).split('.').map(Number);
  const len = Math.max(oldParts.length, newParts.length);

  for (let i = 0; i < len; i++) {
    const o = oldParts[i] ?? 0;
    const n = newParts[i] ?? 0;
    if (n > o) return true;
    if (n < o) return false;
  }
  return false;
}

/**
 * 获取当前安装的版本号
 * APP 端通过 plus.runtime 获取；非 APP 端返回空字符串
 */
export function getCurrentVersion(): Promise<string> {
  return new Promise((resolve) => {
    // #ifdef APP-PLUS
    plus.runtime.getProperty(plus.runtime.appid, (info) => {
      resolve(info.version || '');
    });
    // #endif

    // #ifndef APP-PLUS
    resolve('');
    // #endif
  });
}

/**
 * 获取当前构建版本号 (versionCode)
 */
export function getCurrentVersionCode(): Promise<string> {
  return new Promise((resolve) => {
    // #ifdef APP-PLUS
    plus.runtime.getProperty(plus.runtime.appid, (info) => {
      resolve(info.versionCode || '');
    });
    // #endif

    // #ifndef APP-PLUS
    resolve('');
    // #endif
  });
}

/**
 * 检查应用版本更新
 * @param enforce 是否主动检测（主动检测时无更新也会提示用户）
 * @returns 'updated' 需要更新 | 'latest' 已是最新 | 'error' 检测失败 | 'disabled' 已禁用
 */
export async function checkAppUpdate(enforce = false): Promise<'updated' | 'latest' | 'error' | 'disabled'> {
  const config = getUpgradeConfig();

  // 总开关关闭
  if (!config.enabled) {
    if (enforce) uni.showToast({ title: '当前项目不支持版本检测', icon: 'none' });
    return 'disabled';
  }

  // #ifndef APP-PLUS
  if (enforce) uni.showToast({ title: '当前环境不支持版本检测', icon: 'none' });
  return 'latest';
  // #endif

  // iOS 端：通过 iTunes API 检测
  if (systemInfo.platform === 'ios') {
    return checkIosUpdate(config, enforce);
  }

  // 安卓端：通过后端 API 检测
  return checkAndroidUpdate(config, enforce);
}

/**
 * iOS 版本检测（iTunes Search API）
 */
async function checkIosUpdate(config: ReturnType<typeof getUpgradeConfig>, enforce: boolean): Promise<'updated' | 'latest' | 'error'> {
  if (!config.appleId) {
    if (enforce) uni.showToast({ title: 'iOS 暂未上架应用商店', icon: 'none' });
    return 'latest';
  }

  try {
    const res = await uni.request({
      url: `https://itunes.apple.com/lookup?id=${config.appleId}&country=cn`,
      method: 'GET',
    });
    const data = res.data as ITunesLookupResult;

    if (!data?.results?.length) {
      if (enforce) uni.showToast({ title: '检测失败，请稍后再试', icon: 'none' });
      return 'error';
    }

    const storeInfo = data.results[0];
    const currentVersion = await getCurrentVersion();

    if (compareVersion(currentVersion, storeInfo.version)) {
      const appStoreUrl = config.appStoreUrl || storeInfo.trackViewUrl;
      await navigateToUpgrade(1, {
        version: storeInfo.version,
        version_code: '',
        download_url: appStoreUrl,
        desc: storeInfo.releaseNotes || '修复部分已知问题，优化使用体验',
        force: false,
        hot: false,
      });
      return 'updated';
    }

    if (enforce) uni.showToast({ title: '当前已是最新版本', icon: 'none' });
    return 'latest';
  }
  catch {
    if (enforce) uni.showToast({ title: '检测失败，请稍后再试', icon: 'none' });
    return 'error';
  }
}

/**
 * 安卓版本检测（版本管理后台检查更新接口）
 * 协议 {status, code, msg, data}：code=0 成功；data 为空表示该应用无安卓端或未发布过版本
 */
async function checkAndroidUpdate(config: ReturnType<typeof getUpgradeConfig>, enforce: boolean): Promise<'updated' | 'latest' | 'error'> {
  if (!config.androidApiUrl) {
    if (enforce) uni.showToast({ title: '升级接口未配置', icon: 'none' });
    return 'error';
  }

  try {
    const res = await uni.request({ url: config.androidApiUrl, method: 'GET' });
    const body = res.data as ApiResponse;

    if (body?.code !== 0 || !body.data?.versionNo) {
      if (enforce) uni.showToast({ title: '检测失败，请稍后再试', icon: 'none' });
      return 'error';
    }
    const data = body.data;

    // 判定：接口 versionCode 高于本机原生 versionCode 即有新版本
    const currentVersionCode = await getCurrentVersionCode();
    if (!currentVersionCode || Number(data.versionCode) <= Number(currentVersionCode)) {
      if (enforce) uni.showToast({ title: '当前已是最新版本', icon: 'none' });
      return 'latest';
    }

    await navigateToUpgrade(1, {
      version: data.versionNo,
      version_code: String(data.versionCode),
      download_url: data.apkUrl || data.fileUrl || '',
      desc: data.note || '',
      // 新数据源无强制更新与热更语义
      force: false,
      hot: false,
    });
    return 'updated';
  }
  catch {
    if (enforce) uni.showToast({ title: '检测失败，请稍后再试', icon: 'none' });
    return 'error';
  }
}

/**
 * 保存升级数据并跳转升级页
 */
async function navigateToUpgrade(type: 0 | 1, data: UpgradeData): Promise<void> {
  uni.setStorageSync(UPGRADE_STORAGE_KEY, JSON.stringify({ type, data }));
  uni.navigateTo({ url: `/pages/common/upgrade?type=${type}` });
}

/**
 * 读取升级页所需的升级数据
 */
export function getUpgradeData(): { type: 0 | 1, data: UpgradeData } | null {
  try {
    const raw = uni.getStorageSync(UPGRADE_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  }
  catch { /* ignore */ }
  return null;
}

/**
 * 安卓：跳转到对应的应用市场详情页
 * 根据设备品牌选择对应的 scheme，调用 plus.runtime.openURL()
 */
export function openAndroidMarket(): void {
  // #ifdef APP-PLUS
  const config = getUpgradeConfig();
  const packageName = config.androidPackageName;
  if (!packageName) {
    uni.showToast({ title: '未配置应用包名', icon: 'none' });
    return;
  }

  const brand = (systemInfo.brand || '').toLowerCase();
  let url = '';

  if (brand.includes('huawei') || brand.includes('honor')) {
    // 华为/荣耀
    url = `appmarket://details?id=${packageName}`;
  }
  else if (brand.includes('xiaomi') || brand.includes('redmi')) {
    // 小米/红米
    url = `mimarket://details?id=${packageName}`;
  }
  else if (brand.includes('oppo') || brand.includes('oneplus') || brand.includes('realme')) {
    // OPPO/一加/真我
    url = `oppomarket://details?pkgname=${packageName}`;
  }
  else if (brand.includes('vivo') || brand.includes('iqoo')) {
    // vivo/iQOO
    url = `vivomarket://details?id=${packageName}`;
  }
  else {
    // 通用兜底
    url = `market://details?id=${packageName}`;
  }

  plus.runtime.openURL(url, (err) => {
    // 跳转失败时尝试通用 scheme
    if (err && !url.startsWith('market://')) {
      plus.runtime.openURL(`market://details?id=${packageName}`, () => {
        uni.showToast({ title: '未找到应用市场', icon: 'none' });
      });
    }
    else if (err) {
      uni.showToast({ title: '未找到应用市场', icon: 'none' });
    }
  });
  // #endif
}

/**
 * iOS：跳转到 App Store
 */
export function openAppStore(): void {
  const config = getUpgradeConfig();
  const url = config.appStoreUrl;
  if (!url) {
    uni.showToast({ title: 'App Store 链接未配置', icon: 'none' });
    return;
  }

  // #ifdef APP-PLUS
  plus.runtime.openURL(url);
  // #endif
}
